import { useEffect, useState, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'
import { ArrowUp, WifiOff } from 'lucide-react'
import { recordVisit } from '@/lib/personal'
import { cn } from '@/lib/cn'

/**
 * Small, always-on shell helpers (E-UX). Each one is invisible until it is needed.
 */

/** First tab stop on every page: jumps keyboard and screen-reader users past the navigation. */
export function SkipLink() {
  return (
    <a
      href="#main"
      onClick={(e) => {
        e.preventDefault()
        const main = document.getElementById('main')
        main?.focus()
        main?.scrollIntoView({ block: 'start' })
      }}
      className="sr-only z-[100] rounded-md bg-primary text-sm font-medium text-primary-foreground shadow-e3 focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:px-4 focus:py-2"
    >
      Skip to main content
    </a>
  )
}

/** A calm, in-flow banner while the browser is offline; saving is pointless until it is back. */
export function OfflineBanner() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down) }
  }, [])
  if (online) return null
  return (
    <div role="status" data-testid="offline-banner" className="flex items-center justify-center gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-2 text-sm font-medium text-warning-content">
      <WifiOff className="size-4" aria-hidden />
      You are offline. Changes cannot be saved until your connection is back.
    </div>
  )
}

/** "Back to top" for long pages: appears after ~400 px of scrolling inside the main column. */
export function BackToTop({ scrollRef }: { scrollRef: RefObject<HTMLElement | null> }) {
  const [show, setShow] = useState(false)
  const { pathname } = useLocation()
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const on = () => setShow(el.scrollTop > Math.min(400, el.clientHeight))
    on()
    el.addEventListener('scroll', on, { passive: true })
    return () => el.removeEventListener('scroll', on)
  }, [scrollRef, pathname])
  return (
    <button
      type="button"
      aria-label="Back to top"
      title="Back to top"
      onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })}
      className={cn(
        'fixed bottom-5 right-5 z-40 grid size-10 place-items-center rounded-full border border-border bg-card text-content-secondary shadow-e3 transition-opacity duration-base print:hidden',
        'hover:bg-surface-hover hover:text-foreground',
        show ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      tabIndex={show ? 0 : -1}
      aria-hidden={!show}
    >
      <ArrowUp className="size-4" />
    </button>
  )
}

/**
 * Names the browser tab after the page's heading ("Payroll runs · BIGROI HRMS") and records the
 * page in "recently viewed". Reads the rendered <h1>, so a detail page gets the record's real
 * name, not its id.
 */
export function PageTitleTracker({ mainRef }: { mainRef: RefObject<HTMLElement | null> }) {
  const { pathname, search } = useLocation()
  useEffect(() => {
    const main = mainRef.current
    if (!main) return
    let done = false
    const apply = () => {
      const h1 = main.querySelector('h1')?.textContent?.trim()
      if (!h1 || done) return false
      done = true
      document.title = `${h1} · BIGROI HRMS`
      // Errors / 403 / 404 are not worth revisiting.
      if (!main.querySelector('[data-state="forbidden"], [data-state="not-found"]')) recordVisit(pathname + search, h1)
      return true
    }
    if (apply()) return
    const obs = new MutationObserver(() => { if (apply()) obs.disconnect() })
    obs.observe(main, { childList: true, subtree: true, characterData: true })
    const t = window.setTimeout(() => obs.disconnect(), 15000)
    return () => { obs.disconnect(); window.clearTimeout(t) }
  }, [pathname, search, mainRef])
  return null
}
